import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import { GoogleIcon, LinkedInsmallIcon } from '../../../../assets/icons';
import { easyWebinarLogoLight } from '../../../../assets/images';
import { AI_AUTH_COLORS } from '../constants';
import { config } from '../../../../config';
import { isAuthenticated } from '../../../../routes/ProtectedRoute';
import { getAilaunchRedirectUrl } from '../../../../utils/redirect.util';
import { ChevronRight } from 'lucide-react';
import { toast } from 'react-toastify';
import AuthService from '../../../../services/auth.service';

type SocialProvider = 'Google' | 'Linkedin';

const cardSx = {
  width: '100%',
  maxWidth: '500px',
  borderRadius: '18px',
  border: '1px solid rgba(16, 24, 40, 0.08)',
  boxShadow: '0 20px 56px rgba(16, 24, 40, 0.12)',
  px: { xs: 2.75, sm: 3.8 },
  py: { xs: 3.2, sm: 3.8 },
} as const;

const actionButtonSx = {
  justifyContent: 'space-between',
  textTransform: 'none',
  borderRadius: '10px',
  py: 1.05,
  px: 1.4,
  color: '#111827',
  borderColor: 'rgba(17, 24, 39, 0.14)',
  bgcolor: '#FFFFFF',
  '&:hover': {
    bgcolor: '#F3F4F6',
    borderColor: 'rgba(17, 24, 39, 0.16)',
  },
} as const;

export default function TryAiFlowLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const buildLoginSuccessRedirectUrl = useCallback(
    () => `${getAilaunchRedirectUrl()}&isLogin=true`,
    []
  );

  useEffect(() => {
    if (isAuthenticated()) {
      window.location.href = buildLoginSuccessRedirectUrl();
    }
  }, [buildLoginSuccessRedirectUrl]);

  const isLoginDisabled = useMemo(
    () => email.trim().length === 0 || password.trim().length === 0,
    [email, password]
  );
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('isLogin') === 'true') return;
    const next = new URLSearchParams(searchParams);
    next.set('isLogin', 'true');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  useEffect(() => {
    const payloadParam = searchParams.get("payload");

    if (payloadParam) {
      const decoded = decodeURIComponent(payloadParam);
      const payload = JSON.parse(decoded);

      const try_ai_prompt = payload.try_ai_prompt;
      const webinarType = payload.webinarType;
      const createMode = payload.createMode;
      if (try_ai_prompt) {
        localStorage.setItem("try_ai_prompt", try_ai_prompt);
        localStorage.setItem("webinarType", webinarType);
        localStorage.setItem("createMode", createMode);
        console.log("AI Prompt stored:", try_ai_prompt);
      }
    }
  }, [searchParams]);
  let try_ai_prompt = localStorage.getItem('try_ai_prompt');
  const handleSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isLoginDisabled) return;
    try {
      const response = await AuthService.checkEmailExists(email);
      if (response.data.exists) {
        await AuthService.login({ email, password });
        toast.success('Login successful');
        window.location.href = buildLoginSuccessRedirectUrl();
        return;
      } else {
        toast.error('Email not found. Please create an account.');
        return;
      }
    } catch (error: any) {
      toast.error(error?.message || 'An error occurred. Please try again.');
    }

  }, [email, password, isLoginDisabled, buildLoginSuccessRedirectUrl]);

  const handleSocialSignIn = useCallback((provider: SocialProvider) => {
    const authUrl = config.sso_auth_url;
    // OAuth state: SsoCallback reads isRegistration + flow (token URL + isLogin on AI redirect).
    const stateData = {
      isRegistration: false,
      flow: 'try-ai-flow',
      timestamp: Date.now(),
    };
    const params = new URLSearchParams({
      client_id: config.sso_client_id,
      response_type: 'code',
      scope: 'email openid profile phone',
      redirect_uri: `${window.location.origin}/auth/sso/callback`,
      state: JSON.stringify(stateData),
      identity_provider: provider,
      prompt: 'select_account',
    });
    window.location.href = `${authUrl}?${params.toString()}`;
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        bgcolor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Link to="/try-ai-flow" style={{ display: 'inline-flex', alignItems: 'center' }}>
          <img
            src={easyWebinarLogoLight}
            alt="EasyWebinar"
            style={{ display: 'block', height: 30, width: 'auto' }}
          />
        </Link>
        <Button
          component={Link}
          to="/try-ai-flow/auth"
          variant="contained"
          endIcon={<ChevronRight size={16} />}
          sx={{
            textTransform: 'none',
            borderRadius: '999px',
            px: 1.9,
            py: 0.7,
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#111827',
            bgcolor: '#FFFFFF',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#F9FAFB',
              boxShadow: 'none',
            },
          }}
        >
          Create account
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 4,
        }}
      >
        <Paper elevation={0} sx={cardSx}>
          <Typography
            component="h1"
            sx={{
              textAlign: 'center',
              color: '#111827',
              fontSize: { xs: '2rem', sm: '2.2rem' },
              letterSpacing: '-0.01em',
              mb: 1.1,
            }}
          >
            Welcome back
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              color: '#6B7280',
              fontSize: '0.92rem',
              mb: { xs: '2rem', sm: '2.2rem' },
            }}
          >
            Log in to continue building your webinar funnel
          </Typography>

          <Stack spacing={1.25}>
            <Button
              variant="outlined"
              onClick={() => handleSocialSignIn('Google')}
              sx={{
                ...actionButtonSx,
                transition: 'background 0.12s',
                '&:hover': {
                  bgcolor: '#F3F4F6', // light grey
                  borderColor: 'rgba(17, 24, 39, 0.16)',
                },
                '& .arrow-animate': {
                  transition: 'transform 0.18s cubic-bezier(0.43,0.13,0.23,0.96)',
                },
                '&:hover .arrow-animate': {
                  transform: 'translateX(4px)',
                },
              }}
            >
              <Stack direction="row" spacing={1.1} alignItems="center">
                <GoogleIcon />
                <Typography sx={{ fontSize: '0.89rem', fontWeight: 500 }}>
                  Continue with Google
                </Typography>
              </Stack>
              <ArrowForwardIosRoundedIcon className="arrow-animate" sx={{ fontSize: 12, color: '#6B7280', ml: 0.4 }} />
            </Button>

            <Button
              variant="outlined"
              onClick={() => handleSocialSignIn('Linkedin')}
              sx={{
                ...actionButtonSx,
                transition: 'background 0.12s',
                '&:hover': {
                  bgcolor: '#F3F4F6', // light grey
                  borderColor: 'rgba(17, 24, 39, 0.16)',
                },
                '& .arrow-animate': {
                  transition: 'transform 0.18s cubic-bezier(0.43,0.13,0.23,0.96)',
                },
                '&:hover .arrow-animate': {
                  transform: 'translateX(4px)',
                },
              }}
            >
              <Stack direction="row" spacing={1.1} alignItems="center">
                <LinkedInsmallIcon />
                <Typography sx={{ fontSize: '0.89rem', fontWeight: 500 }}>
                  Continue with LinkedIn
                </Typography>
              </Stack>
              <ArrowForwardIosRoundedIcon className="arrow-animate" sx={{ fontSize: 12, color: '#6B7280', ml: 0.4 }} />
            </Button>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1.2} sx={{ my: 2.25 }}>
            <Divider sx={{ flex: 1, borderColor: 'rgba(17, 24, 39, 0.12)' }} />
            <Typography sx={{ color: '#9CA3AF', fontWeight: 600, fontSize: '0.66rem' }}>
              OR LOGIN WITH EMAIL
            </Typography>
            <Divider sx={{ flex: 1, borderColor: 'rgba(17, 24, 39, 0.12)' }} />
          </Stack>

          <Box component="form" onSubmit={handleSubmit} autoComplete="off">
            <TextField
              fullWidth
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="Use email address"
              variant="outlined"
              size="medium"
              inputProps={{ autoComplete: 'off' }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutlineIcon sx={{ fontSize: 17, color: '#9CA3AF' }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: '#FFFFFF',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#D1D5DB' },
                  '&.Mui-focused fieldset': { borderColor: AI_AUTH_COLORS.primaryBorder },
                },
                '& input': { fontSize: '0.88rem', py: 1.05 },
                mb: 1.45,
              }}
            />

            <TextField
              fullWidth
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Enter password"
              variant="outlined"
              size="medium"
              type={showPassword ? 'text' : 'password'}
              inputProps={{ autoComplete: 'off' }}
              slotProps={{
                input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon sx={{ fontSize: 17, color: '#9CA3AF' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => setShowPassword((prev) => !prev)}
                      onMouseDown={(event) => event.preventDefault()}
                      sx={{ color: '#9CA3AF' }}
                    >
                      {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: '#FFFFFF',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#D1D5DB' },
                  '&.Mui-focused fieldset': { borderColor: AI_AUTH_COLORS.primaryBorder },
                },
                '& input': { fontSize: '0.88rem', py: 1.05 },
                mb: 1,
              }}
            />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1.85,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={rememberMe}
                    onChange={(event) => setRememberMe(event.target.checked)}
                    size="small"
                    sx={{
                      p: 0.5,
                      color: '#D1D5DB',
                      '&.Mui-checked': { color: AI_AUTH_COLORS.primaryBorder },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: '#6B7280', fontSize: '0.76rem' }}>
                    Remember me
                  </Typography>
                }
                sx={{ m: 0, gap: 0.7 }}
              />

              <Typography
                component={Link}
                to="/try-ai-flow/auth/forgot-password"
                sx={{
                  color: AI_AUTH_COLORS.primaryBorder,
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Forgot password?
              </Typography>
            </Box>

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={isLoginDisabled}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                py: 1.18,
                fontSize: '0.92rem',
                fontWeight: 600,
                color: '#FFFFFF',
                bgcolor: AI_AUTH_COLORS.primaryMain,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: AI_AUTH_COLORS.primaryHover,
                  boxShadow: 'none',
                },
                '&.Mui-disabled': {
                  bgcolor: AI_AUTH_COLORS.primaryDisabled,
                  color: '#FFFFFF',
                },
              }}
            >
              Log in
            </Button>
          </Box>

          <Divider sx={{ my: 2.2, borderColor: 'rgba(17, 24, 39, 0.12)' }} />
          <Typography sx={{ textAlign: 'center', color: '#4B5563', fontSize: '0.86rem' }}>
            New to easywebinar?{' '}
            <Typography
              component={Link}
              to="/try-ai-flow/auth"
              sx={{
                display: 'inline',
                color: AI_AUTH_COLORS.primary,
                fontSize: 'inherit',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Create your account
            </Typography>
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
}
